import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useOrganization } from '@/hooks/useOrganization';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import contextPacksService, {
  ContextPack,
  ContextPackCreate,
  ContextPackUpdate,
} from '@/lib/api/contextPacks';
import salesAgentService from '@/lib/api/salesAgent';
import apiClient from '@/lib/api/client';
import type { FileUploadResponse } from '@/lib/api/storage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Plus,
  Pencil,
  Trash2,
  Copy,
  Image,
  Link,
  Loader2,
  PackageOpen,
  Upload,
  X,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataSurface } from '@/components/platform/DataSurface';
import { MetricStrip } from '@/components/platform/MetricStrip';
import { StatusBadge } from '@/components/platform/StatusBadge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const DEMO_BASE_URL = (
  import.meta.env.VITE_DEMO_BASE_URL || 'https://demo.hihobbes.com'
).replace(/\/$/, '');

function buildDemoUrl(demoSlug: string | null | undefined, packId: string): string {
  if (!demoSlug) return `${DEMO_BASE_URL}/?cp=${packId}`;
  return `${DEMO_BASE_URL}/${demoSlug}/${packId}`;
}

// ── Inline editor form ─────────────────────────────────────────────────────

interface PackFormData {
  name: string;
  context_text: string;
  prefill_name: string;
  prefill_email: string;
  share_title: string;
  share_description: string;
  share_thumbnail_url: string;
  is_active: boolean;
}

const emptyForm: PackFormData = {
  name: '',
  context_text: '',
  prefill_name: '',
  prefill_email: '',
  share_title: '',
  share_description: '',
  share_thumbnail_url: '',
  is_active: true,
};

function packToForm(p: ContextPack): PackFormData {
  return {
    name: p.name,
    context_text: p.context_text ?? '',
    prefill_name: p.prefill_name ?? '',
    prefill_email: p.prefill_email ?? '',
    share_title: p.share_title ?? '',
    share_description: p.share_description ?? '',
    share_thumbnail_url: p.share_thumbnail_url ?? '',
    is_active: p.is_active,
  };
}

function formToCreate(f: PackFormData): ContextPackCreate {
  return {
    name: f.name.trim(),
    context_text: f.context_text.trim() || null,
    prefill_name: f.prefill_name.trim() || null,
    prefill_email: f.prefill_email.trim() || null,
    prefill_phone: null,
    share_title: f.share_title.trim() || null,
    share_description: f.share_description.trim() || null,
    share_thumbnail_url: f.share_thumbnail_url.trim() || null,
    is_active: f.is_active,
  };
}

function formToUpdate(f: PackFormData): ContextPackUpdate {
  return formToCreate(f);
}

function FieldRequirement({ required = false }: { required?: boolean }) {
  return (
    <span className="ml-1 text-xs font-normal text-muted-foreground">
      {required ? 'Required' : 'Optional'}
    </span>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function TableSkeleton() {
  return (
    <>
      {[1, 2, 3, 4, 5].map((index) => (
        <TableRow key={index}>
          <TableCell>
            <div className="space-y-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-64 max-w-full" />
            </div>
          </TableCell>
          <TableCell><Skeleton className="h-5 w-16" /></TableCell>
          <TableCell><Skeleton className="h-4 w-32" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-8 w-24" /></TableCell>
        </TableRow>
      ))}
    </>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function ContextPacksPage() {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch list
  const {
    data: packsResponse,
    isLoading,
  } = useQuery({
    queryKey: ['context-packs', orgId],
    queryFn: () => contextPacksService.list(orgId!),
    enabled: !!orgId,
  });
  const packs: ContextPack[] = packsResponse?.data ?? [];

  // Fetch sales agent to get demo_slug for shareable URLs
  const { data: agentResponse } = useQuery({
    queryKey: ['sales-agent', orgId],
    queryFn: () => salesAgentService.getSalesAgent(orgId!),
    enabled: !!orgId,
  });
  const demoSlug = agentResponse?.data?.demo_slug;

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPack, setEditingPack] = useState<ContextPack | null>(null);
  const [form, setForm] = useState<PackFormData>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<ContextPack | null>(null);
  const [isUploadingShareThumbnail, setIsUploadingShareThumbnail] = useState(false);
  const shareThumbnailInputRef = React.useRef<HTMLInputElement>(null);

  const openCreate = () => {
    setEditingPack(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (pack: ContextPack) => {
    setEditingPack(pack);
    setForm(packToForm(pack));
    setDialogOpen(true);
  };

  // Mutations
  const createMut = useMutation({
    mutationFn: (data: ContextPackCreate) => contextPacksService.create(orgId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['context-packs', orgId] });
      setDialogOpen(false);
      toast({ title: 'Context pack created' });
    },
    onError: () => toast({ title: 'Failed to create context pack', variant: 'destructive' }),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ContextPackUpdate }) =>
      contextPacksService.update(orgId!, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['context-packs', orgId] });
      setDialogOpen(false);
      toast({ title: 'Context pack updated' });
    },
    onError: () => toast({ title: 'Failed to update context pack', variant: 'destructive' }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => contextPacksService.remove(orgId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['context-packs', orgId] });
      setDeleteTarget(null);
      toast({ title: 'Context pack deleted' });
    },
    onError: () => toast({ title: 'Failed to delete context pack', variant: 'destructive' }),
  });

  const handleSave = () => {
    if (!form.name.trim()) return;
    if (editingPack) {
      updateMut.mutate({ id: editingPack.id, data: formToUpdate(form) });
    } else {
      createMut.mutate(formToCreate(form));
    }
  };

  const handleShareThumbnailUpload = async (file: File) => {
    if (!orgId) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast({ title: 'Invalid file type', description: 'Allowed formats: PNG, JPG, WebP, GIF, SVG', variant: 'destructive' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Maximum file size is 5 MB', variant: 'destructive' });
      return;
    }

    setIsUploadingShareThumbnail(true);
    try {
      const formPayload = new FormData();
      formPayload.append('organizationId', orgId);
      formPayload.append('fileType', 'share_thumbnail');
      formPayload.append('file', file);
      if (editingPack?.id) formPayload.append('agentKey', editingPack.id);
      const res = await apiClient.post<FileUploadResponse>('/api/storage/upload', formPayload);
      if (!res.success || !res.data?.url) {
        throw new Error(res.error || 'Upload failed');
      }
      setForm((prev) => ({ ...prev, share_thumbnail_url: res.data!.url }));
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err instanceof Error ? err.message : 'Failed to upload image',
        variant: 'destructive',
      });
    } finally {
      setIsUploadingShareThumbnail(false);
    }
  };

  const isSaving = createMut.isPending || updateMut.isPending;

  const copyUrl = (pack: ContextPack) => {
    const url = buildDemoUrl(demoSlug, pack.id);
    navigator.clipboard.writeText(url);
    toast({ title: 'Demo URL copied to clipboard' });
  };

  if (!orgId) return null;

  const activeCount = packs.filter((pack) => pack.is_active).length;
  const withContextCount = packs.filter((pack) => Boolean(pack.context_text?.trim())).length;
  const withVisitorFieldsCount = packs.filter((pack) => (
    Boolean(pack.prefill_name?.trim()) || Boolean(pack.prefill_email?.trim())
  )).length;

  return (
    <>
      <Helmet>
        <title>Context Packs | Hobbes</title>
      </Helmet>
      <div className="flex min-h-0 flex-col">
        <PageHeader
          icon={PackageOpen}
          title="Context Packs"
          titleTooltip="Create personalized demo links with visitor context and pre-filled name or email."
        />

        <div className="min-w-0 flex-1 overflow-y-auto px-4 pb-8 pt-8 sm:px-6 md:pt-10">
          <div className="mx-auto w-full max-w-[960px]">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h1 className="text-[24px] font-medium leading-8 text-foreground">Personal Context</h1>
              </div>
              <Button onClick={openCreate}>
                <Plus className="mr-2 h-4 w-4" /> New Context Pack
              </Button>
            </div>

            <MetricStrip
              items={[
                {
                  label: 'Total',
                  description: 'All context packs in this organization.',
                  value: isLoading ? '...' : packs.length.toLocaleString(),
                  meta: 'Demo links',
                },
                {
                  label: 'Active',
                  description: 'Context packs currently available for demo links.',
                  value: isLoading ? '...' : activeCount.toLocaleString(),
                  meta: `${Math.max(0, packs.length - activeCount).toLocaleString()} inactive`,
                },
                {
                  label: 'With context',
                  description: 'Packs that add extra agent context for a visitor.',
                  value: isLoading ? '...' : withContextCount.toLocaleString(),
                  meta: 'Agent instructions',
                },
                {
                  label: 'Visitor fields',
                  description: 'Packs with pre-filled visitor name or email.',
                  value: isLoading ? '...' : withVisitorFieldsCount.toLocaleString(),
                  meta: 'Name or email',
                },
              ]}
            />

            <div className="pt-6">
              <DataSurface>
                <div className="flex items-center justify-between gap-3 border-b border-border/80 px-4 py-3">
                  <span className="text-sm font-medium text-foreground">Context packs</span>
                  <span className="text-sm text-muted-foreground">
                    {isLoading ? 'Loading...' : `${packs.length.toLocaleString()} total`}
                  </span>
                </div>
                <Table className="table-fixed">
                  <TableHeader className="bg-muted/30">
                    <TableRow className="border-b border-border/70 hover:bg-transparent">
                      <TableHead className="w-[34%]">Pack</TableHead>
                      <TableHead className="w-[96px]">Status</TableHead>
                      <TableHead>Visitor</TableHead>
                      <TableHead className="w-[116px]">Updated</TableHead>
                      <TableHead className="w-[112px] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableSkeleton />
                    ) : packs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="h-64">
                          <div className="flex flex-col items-center justify-center text-center">
                            <PackageOpen className="mb-4 h-12 w-12 text-muted-foreground/40" />
                            <h3 className="text-base font-medium text-foreground">No context packs yet</h3>
                            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                              Create a personalized demo link that pre-fills visitor information and gives the agent extra context.
                            </p>
                            <Button className="mt-4" onClick={openCreate}>
                              <Plus className="mr-2 h-4 w-4" /> Create your first
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      packs.map((pack) => {
                        const visitorFields = [
                          pack.prefill_name ? `Name: ${pack.prefill_name}` : null,
                          pack.prefill_email ? `Email: ${pack.prefill_email}` : null,
                        ].filter(Boolean);
                        return (
                          <TableRow
                            key={pack.id}
                            className="border-b border-border/70 transition-colors last:border-0 hover:bg-accent/50"
                          >
                            <TableCell>
                              <div className="min-w-0 space-y-1">
                                <div className="truncate font-medium text-heading">{pack.name}</div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <StatusBadge tone={pack.is_active ? 'success' : 'pending'} showDot>
                                {pack.is_active ? 'Active' : 'Inactive'}
                              </StatusBadge>
                            </TableCell>
                            <TableCell>
                              <div className="max-w-[260px] truncate text-muted-foreground">
                                {visitorFields.length > 0 ? visitorFields.join(' · ') : '—'}
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatDate(pack.updated_at)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="icon" onClick={() => copyUrl(pack)} title="Copy demo URL">
                                  <Link className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => openEdit(pack)} title="Edit">
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(pack)} title="Delete">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </DataSurface>
            </div>
          </div>
        </div>
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[calc(100vh_-_2rem)] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPack ? 'Edit Context Pack' : 'New Context Pack'}</DialogTitle>
            <DialogDescription>
              {editingPack
                ? 'Update the context pack details.'
                : 'Create a personalized demo link with visitor context and pre-filled name or email.'}
            </DialogDescription>
          </DialogHeader>

          <div className="min-w-0 space-y-4 py-2">
            <div className="min-w-0 space-y-2">
              <Label htmlFor="cp-name">
                Name
                <FieldRequirement required />
              </Label>
              <Input
                id="cp-name"
                placeholder="e.g. Acme Corp Demo"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <div className="min-w-0 space-y-2">
              <Label htmlFor="cp-context">
                Context
                <FieldRequirement />
              </Label>
              <Textarea
                id="cp-context"
                placeholder="Any extra context the agent should know about this visitor…"
                rows={4}
                value={form.context_text}
                onChange={(e) => setForm({ ...form, context_text: e.target.value })}
              />
            </div>

            <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="min-w-0 space-y-2">
                <Label htmlFor="cp-prefill-name">
                  Visitor Name
                  <FieldRequirement />
                </Label>
                <Input
                  id="cp-prefill-name"
                  placeholder="Jane Doe"
                  value={form.prefill_name}
                  onChange={(e) => setForm({ ...form, prefill_name: e.target.value })}
                />
              </div>
              <div className="min-w-0 space-y-2">
                <Label htmlFor="cp-prefill-email">
                  Visitor email
                  <FieldRequirement />
                </Label>
                <Input
                  id="cp-prefill-email"
                  placeholder="jane@acme.com"
                  value={form.prefill_email}
                  onChange={(e) => setForm({ ...form, prefill_email: e.target.value })}
                />
              </div>
            </div>

            <div className="min-w-0 space-y-2">
              <Label htmlFor="cp-share-title">
                Custom Link Title
                <FieldRequirement />
              </Label>
              <Input
                id="cp-share-title"
                placeholder="Acme operations demo"
                value={form.share_title}
                onChange={(e) => setForm({ ...form, share_title: e.target.value })}
              />
            </div>

            <div className="min-w-0 space-y-2">
              <Label htmlFor="cp-share-description">
                Custom Link Description
                <FieldRequirement />
              </Label>
              <Textarea
                id="cp-share-description"
                placeholder="A personalized walkthrough for the team."
                rows={3}
                value={form.share_description}
                onChange={(e) => setForm({ ...form, share_description: e.target.value })}
              />
            </div>

            <div className="min-w-0 space-y-2">
              <Label htmlFor="cp-share-thumbnail">
                Custom Link Thumbnail
                <FieldRequirement />
              </Label>
              {form.share_thumbnail_url ? (
                <div className="relative w-full overflow-hidden rounded-lg border border-border/70">
                  <div className="aspect-video w-full bg-muted">
                    <img
                      src={form.share_thumbnail_url}
                      alt="Custom link thumbnail"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-2 h-7 w-7 rounded-full bg-destructive text-destructive-foreground"
                    onClick={() => setForm({ ...form, share_thumbnail_url: '' })}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <div className="flex aspect-video w-full items-center justify-center rounded-lg border border-dashed border-border bg-muted/40 text-muted-foreground">
                  <div className="text-center">
                    <Image className="mx-auto h-7 w-7 text-muted-foreground/60" />
                    <p className="mt-2 text-sm">Uses default link thumbnail</p>
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <Input
                  id="cp-share-thumbnail"
                  type="url"
                  placeholder="https://your-thumbnail-url.com/link-preview.jpg"
                  value={form.share_thumbnail_url}
                  onChange={(e) => setForm({ ...form, share_thumbnail_url: e.target.value })}
                  className="min-w-[240px] flex-1"
                />
                <input
                  ref={shareThumbnailInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp,.gif"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleShareThumbnailUpload(file);
                    e.target.value = '';
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={isUploadingShareThumbnail}
                  onClick={() => shareThumbnailInputRef.current?.click()}
                >
                  {isUploadingShareThumbnail ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                  {isUploadingShareThumbnail ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="cp-active"
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
              />
              <Label htmlFor="cp-active">Active</Label>
            </div>

            {/* Shareable URL preview (only for existing packs) */}
            {editingPack && (
              <div className="min-w-0 space-y-1">
                <Label className="text-xs text-muted-foreground">Demo URL</Label>
                <div className="flex min-w-0 items-center gap-2">
                  <code className="block min-w-0 flex-1 truncate rounded-md bg-muted px-3 py-2 text-xs">
                    {buildDemoUrl(demoSlug, editingPack.id)}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={() => copyUrl(editingPack)}
                  >
                    <Copy className="h-3 w-3 mr-1" /> Copy
                  </Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={!form.name.trim() || isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingPack ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Context Pack</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &ldquo;{deleteTarget?.name}&rdquo;? Existing links using this pack will stop working.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
              disabled={deleteMut.isPending}
            >
              {deleteMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
